<template>
  <div class="tw:flex tw:flex-col tw:p-4">
    <div class="tw:mb-2 tw:flex tw:items-center tw:gap-1 tw:font-bold">
      {{ getLocaleText("stream_filters_impact_score.label") }}

      <QIcon name="info">
        <StreemTooltip
          anchor="top middle"
          self="bottom middle"
        >
          {{ getLocaleText("stream_filters_impact_score.tooltip") }}
        </StreemTooltip>
      </QIcon>
    </div>
    <div class="tw:flex tw:justify-between tw:gap-4">
      <div
        v-for="impactScoreOption in impactScoreOptions"
        :key="impactScoreOption.key"
      >
        <Checkbox
          v-model="selectedImpactScore"
          :color="impactScoreOption.key"
          :data-testid="`option-${impactScoreOption.key}`"
          dense
          :label="impactScoreOption.label"
          new-design
          :value="impactScoreOption.key"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import StreemTooltip from "shared/components/base/StreemTooltip.vue";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import { type StreamFiltersMinMax } from "shared/helpers/StreamFilters";

interface ImpactScoreOption {
  key: string;
  label?: string;
  max: number;
  min: number;
}

const impactScore = defineModel<StreamFiltersMinMax[]>("impactScore", {
  default: () => [],
});

const IMPACT_SCORE_OPTIONS: ImpactScoreOption[] = [
  {
    key: "low",
    max: 200,
    min: 0,
  },
  {
    key: "average",
    max: 350,
    min: 200.01,
  },
  {
    key: "excellent",
    max: 3000,
    min: 350.01,
  },
];

const impactScoreOptions = computed<ImpactScoreOption[]>(() =>
  IMPACT_SCORE_OPTIONS.map((option) => ({
    ...option,
    label: getLocaleText(
      `stream_filters_impact_score.range_options.${option.key}`,
      { min: option.min, max: option.max }
    ),
  }))
);

const selectedImpactScore = computed<ImpactScoreOption["key"][]>({
  get() {
    return impactScoreOptions.value
      .filter((sentimentOption) =>
        impactScore.value.some((range) => {
          const { min, max } = sentimentOption;

          return (
            range.min === min ||
            range.max === max ||
            (Number(range.min) < min && Number(range.max) > max)
          );
        })
      )
      .map((sentimentOption) => sentimentOption.key);
  },
  set(values: ImpactScoreOption["key"][]) {
    const newImpactScore: StreamFiltersMinMax[] = [];

    impactScoreOptions.value.forEach((impactScoreOption, index) => {
      if (!values.includes(impactScoreOption.key)) {
        return;
      }

      const { min, max } = impactScoreOption;
      const previousOption = impactScoreOptions.value[index - 1];

      if (previousOption && values.includes(previousOption.key)) {
        const previousRange = newImpactScore[newImpactScore.length - 1];
        previousRange.max = Math.max(max, Number(previousRange.max));
        previousRange.min = Math.min(min, Number(previousRange.min));
      } else {
        newImpactScore.push({ min, max });
      }
    });

    impactScore.value = newImpactScore;
  },
});
</script>
